import { notification } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { signedUploadUrl } from '../../../../../actions';
import { getOpenedTaskId } from '../../../../../redux/general/selectors';
import { getOpenedTaskFiles, getOpenedTaskTitle } from '../../../../../redux/tasks/taskEntities/selectors';
import { editTaskAction, uploadTaskUrlAction } from '../../../../../redux/tasks/actions';
import Loader from '../../../atoms/global/Loader';

const MAX_BYTES = 1 * 1024 ** 3;

const TaskDetailsFileInput = ({ isDropAreaShown, taskFiles, taskId, taskTitle }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  const uploadFiles = useCallback(async (files) => {
    const fileList = [];
    for (const file of files) {
      setIsLoading(true);
      const uploadTaskResponse = await dispatch(
        uploadTaskUrlAction(
          {
            fileName: file.name,
            taskId,
            type: file?.type,
            fileSize: file?.size,
          },
          undefined,
          intl,
        ),
      );
      const fileId = uploadTaskResponse?.fileId;
      const signedUrl = uploadTaskResponse?.signedUrl;
      if (signedUrl) {
        fileList.push(fileId);
        await dispatch(signedUploadUrl(signedUrl, file));
      }
    }

    let allFiles = fileList;

    if (taskFiles) {
      allFiles = fileList.concat(taskFiles || []);
    }

    dispatch(editTaskAction({
      _id: taskId,
      taskTitle,
      files: allFiles,
      attachments: [],
    }, () => {
      setIsLoading(false)
    }, () => {
      setIsLoading(false)
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFiles, taskId, taskTitle]);

  const onFileInputChange = useCallback(async (e) => {
    try {
      const rawFiles = fileInputRef.current?.files;
      if (!rawFiles) return;

      // Convert FileList to array and filter
      const filesArray = Array.from(rawFiles);
      const validFiles = filesArray.filter((file) => {
        if (file.size > MAX_BYTES) {
          notification.error({
            message: `"${file.name}" is larger than 1GB and was skipped.`,
            placement: 'bottomRight',
          });
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        return;
      }

      await uploadFiles(validFiles);
    } catch (error) {
      notification.error({
        message: 'Failed to upload files.',
        placement: 'bottomRight',
      });
    } finally {
      // Optional: clear the input so same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [uploadFiles]);

  return (
    <>
      <input
        style={{ display: 'none' }}
        id="file-input"
        ref={fileInputRef}
        type="file"
        multiple
        onChange={onFileInputChange}
      />
      {isDropAreaShown && (
        <div
          className="d-flex align-center mx-2 w-full mt-1"
          style={{
            height: '50px',
            borderRadius: 10,
            border: '1px solid #D6D6D6',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => {
            fileInputRef.current.click();
          }}
        >
          <div className="d-flex">
            {isLoading ? (
              <Loader size={15} isLoading={isLoading} type="clip" />
            ) : (
              <>
                <img
                  src="/images/icons/Upload.svg"
                  alt=""
                  style={{
                    height: '20px',
                    width: '20px',
                    marginRight: 10,
                  }}
                />
                <div className="font-14">
                  <FormattedMessage
                    id="DROP_FILES_OR_BROWSE"
                    defaultMessage="Drop files to attach, or browse"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  taskFiles: getOpenedTaskFiles(state),
  taskId: getOpenedTaskId(state),
  taskTitle: getOpenedTaskTitle(state),
});

export default connect(mapStateToProps)(TaskDetailsFileInput);
